import React, { useEffect, useState } from "react";
import { collection, getDocs, query } from "firebase/firestore";
import { db } from "../firebase";
import Navbar from "../components/navbar";
import Footer from "../Sections/footer";

const AllExecomPage = () => {
    const [execomYears, setExecomYears] = useState([]);
    const [selectedYear, setSelectedYear] = useState("");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        async function fetchAllExecoms() {
            try {
                setLoading(true);
                const execomCol = collection(db, "execom");
                const snapshot = await getDocs(query(execomCol));
                const docs = snapshot.docs.map((doc) => ({
                    id: doc.id,
                    ...doc.data(),
                }));

                // Sort: current first, then by year descending
                docs.sort((a, b) => {
                    if (a.isCurrent && !b.isCurrent) return -1;
                    if (!a.isCurrent && b.isCurrent) return 1;
                    return (b.year || "").localeCompare(a.year || "");
                });

                setExecomYears(docs);

                // Default to the current year
                const currentDoc = docs.find((d) => d.isCurrent) || docs[0];
                if (currentDoc) {
                    setSelectedYear(currentDoc.year);
                }
            } catch (error) {
                console.error("Error fetching execom data:", error);
            } finally {
                setLoading(false);
            }
        }

        fetchAllExecoms();
    }, []);

    const selectedDoc = execomYears.find((d) => d.year === selectedYear);
    const members = selectedDoc?.members || [];

    // Full page loading component
    const FullPageLoader = () => (
        <div className="min-h-screen bg-black flex flex-col justify-center items-center">
            <div className="w-16 h-16 text-red-500 mb-6">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M13 10V3L4 14h7v7l9-11h-7z" strokeDasharray="80" strokeDashoffset="80">
                        <animate attributeName="stroke-dashoffset" values="80;0;80" dur="1s" repeatCount="indefinite"></animate>
                    </path>
                </svg>
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Loading Execom</h2>
            <p className="text-gray-400">Please wait while we fetch all executive committees...</p>
        </div>
    );

    if (loading) {
        return <FullPageLoader />;
    }

    return (
        <div className="min-h-screen bg-black text-white">
            <Navbar />

            {/* Header */}
            <header className="bg-black border-b border-red-500/30 py-8">
                <div className="max-w-6xl mx-auto mt-16 px-6 text-center">
                    <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
                        <span className="text-white drop-shadow-lg">Executive </span>
                        <span className="text-red-500 drop-shadow-lg">Committee</span>
                    </h1>
                    <div className="flex justify-center mb-6">
                        <div className="w-24 h-1 bg-gradient-to-r from-transparent via-red-500 to-transparent rounded-full"></div>
                    </div>
                    <p className="text-gray-400 text-lg md:text-xl max-w-3xl mx-auto leading-relaxed font-light">
                        Explore all the executive committees across the years
                    </p>

                    {/* Year Dropdown */}
                    {execomYears.length > 0 && (
                        <div className="mt-8 flex justify-center">
                            <div className="relative">
                                <select
                                    value={selectedYear}
                                    onChange={(e) => setSelectedYear(e.target.value)}
                                    className="appearance-none bg-gray-900/80 border border-red-500/40 text-white px-6 py-3 pr-12 rounded-full text-lg font-semibold cursor-pointer hover:border-red-500/70 focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-500/20 transition-all duration-300 shadow-lg shadow-red-500/10"
                                >
                                    {execomYears.map((doc) => (
                                        <option key={doc.id} value={doc.year} className="bg-gray-900 text-white">
                                            {doc.year}{doc.isCurrent ? " (Current)" : ""}
                                        </option>
                                    ))}
                                </select>
                                {/* Custom dropdown arrow */}
                                <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
                                    <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </header>

            {/* Members Grid */}
            <main className="py-12">
                <div className="max-w-7xl mx-auto px-6">
                    {members.length === 0 ? (
                        <div className="text-center py-20">
                            <p className="text-gray-400 text-xl">No members found for this year.</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                            {members.map((member, index) => (
                                <div
                                    key={`member-${index}`}
                                    className="group bg-gradient-to-br from-black to-gray-900 border border-gray-700 rounded-xl overflow-hidden hover:shadow-2xl hover:shadow-red-500/20 hover:scale-105 transition-all duration-500 hover:-translate-y-2"
                                >
                                    {/* Member Image */}
                                    <div className="relative overflow-hidden h-64">
                                        <img
                                            src={member.imageUrl || "https://static.vecteezy.com/system/resources/previews/041/642/170/non_2x/ai-generated-portrait-of-handsome-smiling-young-man-with-folded-arms-isolated-free-png.png"}
                                            alt={`${member.name} - ${member.role}`}
                                            className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-700"
                                        />

                                        {/* Role Badge */}
                                        <div className="absolute top-3 left-3">
                                            <span className="px-3 py-1 bg-red-700/90 backdrop-blur-sm text-white text-xs font-medium rounded-full">
                                                {member.role}
                                            </span>
                                        </div>

                                        {/* LinkedIn overlay on hover */}
                                        {member.linkedin && (
                                            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                                <a
                                                    href={member.linkedin}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-blue-400 text-sm hover:text-blue-300 transition-colors"
                                                >
                                                    LinkedIn →
                                                </a>
                                            </div>
                                        )}

                                        {/* Hover glow */}
                                        <div className="absolute inset-0 bg-gradient-to-br from-red-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                                    </div>

                                    {/* Member Info */}
                                    <div className="p-4">
                                        <h3 className="text-lg font-bold text-white group-hover:text-red-100 transition-colors duration-300">
                                            {member.name}
                                        </h3>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </main>

            <Footer />
        </div>
    );
};

export default AllExecomPage;
